import {
  type ItineraryStop,
  type LegDistance,
  type PortRecord,
  pairKey,
  resolveLegDistance,
  routeTotal,
  type SeaNetwork,
  seaRoute,
  suggestPortOrder,
} from '@felix/domain';
import type { CompanyPort, PortDistance } from './types.js';

/**
 * Seferin bacakları ve sıra önerisi (ADR 0048 §3, §5): istemci paketli veriyle hesaplar; şirket
 * tablosundaki değer (elle, içe aktarılan, saklanan hesap) önce gelir. Saf: veri parametreyle.
 */

export interface PortCatalog {
  ports: readonly PortRecord[];
  byCode: ReadonlyMap<string, PortRecord>;
  network: SeaNetwork;
}

/** Paketli liste + şirket limanları (şirket limanı paketli kodu gölgeleyemez; ADR 0048 §3). */
export function catalogOf(
  packaged: readonly PortRecord[],
  network: SeaNetwork,
  company: readonly CompanyPort[],
): PortCatalog {
  const extra: PortRecord[] = company.map((port) => ({
    code: port.code,
    name: port.name,
    country: port.country,
    lat: port.lat,
    lon: port.lon,
    altNames: [],
    size: null,
    source: 'company',
  }));
  const ports = [...extra, ...packaged];
  const byCode = new Map<string, PortRecord>();
  for (const port of ports) if (!byCode.has(port.code)) byCode.set(port.code, port);
  return { ports, byCode, network };
}

export interface RouteLeg extends LegDistance {
  fromKey: string;
  toKey: string;
  /** Mesafe neden yok: limanın kodu yok ya da ağda bağlantı bulunamadı. */
  reason?: 'NO_CODE' | 'NO_ROUTE' | undefined;
}

const round = (nm: number) => Math.round(nm * 10) / 10;

function storedMap(distances: readonly PortDistance[]) {
  return new Map(distances.map((d) => [pairKey(d.portA, d.portB), d]));
}

function legBetween(
  a: string | null,
  b: string | null,
  catalog: PortCatalog,
  stored: ReadonlyMap<string, PortDistance>,
): LegDistance & { reason?: RouteLeg['reason'] | undefined } {
  if (!a || !b) return { distanceNm: null, source: null, stored: false, reason: 'NO_CODE' };
  if (a === b) return { distanceNm: 0, source: null, stored: false };
  const from = catalog.byCode.get(a);
  const to = catalog.byCode.get(b);
  const leg = resolveLegDistance(stored.get(pairKey(a, b)), () => {
    if (!from || !to) return null;
    const route = seaRoute(catalog.network, [from.lon, from.lat], [to.lon, to.lat]);
    return route ? round(route.distanceNm) : null;
  });
  if (leg.distanceNm === null) {
    return { ...leg, reason: !from || !to ? 'NO_CODE' : 'NO_ROUTE' };
  }
  return leg;
}

/** Bacak bacak mesafe ve toplam; bir bacak eksikse toplam yoktur. */
export function routeLegs(
  stops: readonly ItineraryStop[],
  catalog: PortCatalog,
  distances: readonly PortDistance[],
): { legs: RouteLeg[]; totalNm: number | null; complete: boolean } {
  const stored = storedMap(distances);
  const legs: RouteLeg[] = [];
  for (let i = 1; i < stops.length; i++) {
    const from = stops[i - 1] as ItineraryStop;
    const to = stops[i] as ItineraryStop;
    legs.push({
      fromKey: from.key,
      toKey: to.key,
      ...legBetween(from.portCode, to.portCode, catalog, stored),
    });
  }
  const complete = legs.every((leg) => leg.distanceNm !== null);
  const totalNm = complete ? legs.reduce((sum, leg) => sum + (leg.distanceNm ?? 0), 0) : null;
  return { legs, totalNm, complete };
}

export type RouteOrderSuggestion =
  | { kind: 'nothing' }
  | { kind: 'missing-code' }
  | { kind: 'no-route' }
  | {
      kind: 'ok';
      /** Ara limanların önerilen sırası (güzergâh anahtarları). */
      order: string[];
      currentNm: number;
      suggestedNm: number;
      exact: boolean;
    };

/** Ara limanlar için mesafeye göre sıra önerisi; başlangıç ve bitiş sabit (ADR 0048 §5). */
export function routeOrderSuggestion(
  stops: readonly ItineraryStop[],
  catalog: PortCatalog,
  distances: readonly PortDistance[],
): RouteOrderSuggestion {
  if (stops.length < 4) return { kind: 'nothing' };
  if (stops.some((stop) => !stop.portCode || !catalog.byCode.has(stop.portCode))) {
    return { kind: 'missing-code' };
  }
  const stored = storedMap(distances);
  const matrix = stops.map((a) =>
    stops.map((b) => legBetween(a.portCode, b.portCode, catalog, stored).distanceNm),
  );
  const indices = stops.map((_, i) => i);
  const middle = indices.slice(1, -1);
  const current = routeTotal(matrix, indices);
  const suggestion = suggestPortOrder(matrix, { start: 0, end: stops.length - 1, middle });
  if (current === null || !suggestion) return { kind: 'no-route' };
  return {
    kind: 'ok',
    order: suggestion.order.map((i) => (stops[i] as ItineraryStop).key),
    currentNm: current,
    suggestedNm: suggestion.totalNm,
    exact: suggestion.exact,
  };
}
